/**
 * Plays the brain's decisions on the model: loops and one-shots crossfaded by weight on one AnimationMixer, the face
 * (shape keys, or the jaw and eye bones when there are none), the look-at on the head and eyes, and the procedural
 * stand-ins for whatever clip the model lacks. Everything procedural is added after the mixer, on top of the pose the
 * mixer left, so nothing accumulates frame to frame.
 *
 * Two jumps this avoids (Kevin, 2026-10-25: "Bram glitches … jumps to a static position"): three's PropertyMixer only
 * writes a node when its mixed value changed since the last frame, so a node put back to its rest pose before the
 * mixer stayed at rest whenever the clips held still (a held `point` once the idle had faded out: the pointing arm
 * dropped 66° to the bind pose for the rest of the tip; the first frame after a re-attach, dt 0) — the procedural
 * layer now restores the mixer's own last output instead. And a one-shot made again while it still carried weight (a
 * pointing tip after a pointing tip, the held point re-made on a quiet spell, a nudge) was reset in place, snapping to
 * its first frame — it now starts on a twin action and crossfades over the old one.
 */
import * as THREE from "three";
import type { GuideKind } from "../guide";
import { CLIP_SECONDS, type BramBone, type BramMorph } from "./bramContract";
import type { BramModel } from "./bramAsset";
import { BlinkScheduler, BramBrain, type BramFrame, type BramMood } from "./bramBrain";
import { LOOK_AHEAD, LookWander, damp, lookAngles, type LookAngles } from "./bramLook";
import { ENTER_MS, HOP_MS, REST, entrance, gesture, hop, talkBob, type Offsets } from "./bramMotion";
import { clipSeconds } from "./bramRig";
import type { Speech } from "./bramSpeech";

/** Crossfade speeds (e-foldings a second): loops blend slowly, a gesture comes in quick. */
const LOOP_RATE = 7;
const ACTION_IN_RATE = 14;
const ACTION_OUT_RATE = 6;
/** Below this a faded action is stopped. */
const WEIGHT_EPS = 0.01;
/** Copies of one one-shot that may overlap (a re-made gesture crossfades over the one still fading). */
const MAX_TWINS = 3;
/** How fast the head, the eyes and the face follow their targets. */
const HEAD_RATE = 6;
const EYE_RATE = 16;
const FACE_RATE = 8;
const MOUTH_RATE = 30;
/** A model with no mouth keys but a jaw opens it this far at `mouth_open` 1. */
const JAW_MAX = THREE.MathUtils.degToRad(14);
/** Eye bones without a `blink` key squash to this at a full blink. */
const EYE_BLINK_SCALE = 0.1;
/** A procedural arm raise at 1 swings the upper arm this far out and up. */
const ARM_MAX = THREE.MathUtils.degToRad(150);
/** How long after a change the look is still settling (the loop keeps the active frame rate meanwhile). */
const SETTLE_MS = 700;

const X = new THREE.Vector3(1, 0, 0), Y = new THREE.Vector3(0, 1, 0), Z = new THREE.Vector3(0, 0, 1);
const qa = new THREE.Quaternion(), qb = new THREE.Quaternion(), qc = new THREE.Quaternion();
const ma = new THREE.Matrix4(), mb = new THREE.Matrix4(), va = new THREE.Vector3();

/** A node the procedural layer turns: the transform the mixer last left on it (rest at first), and its pivot (own space). */
interface Driven {
  readonly node: THREE.Object3D; readonly pos: THREE.Vector3; readonly quat: THREE.Quaternion; readonly scale: THREE.Vector3; readonly pivot: THREE.Vector3;
  /** Which side of him it hangs on, on screen (+1 right, −1 left): an arm raises outward, away from his face. */
  readonly side: number;
}

/**
 * Where a node turns. A bone turns about its joint (its origin). A rigid part (the stand-in's head, its arms) may
 * have its origin anywhere, often the model's feet: it turns about the end of its box that meets the body — the
 * bottom of a head (the neck), the top of an arm (the shoulder), the middle of anything else.
 */
function pivotOf(bone: BramBone, node: THREE.Object3D): THREE.Vector3 {
  if ((node as THREE.Bone).isBone) return new THREE.Vector3();
  const box = new THREE.Box3().setFromObject(node);
  if (box.isEmpty()) return new THREE.Vector3();
  const c = box.getCenter(new THREE.Vector3());
  if (bone === "head") c.y = box.min.y;
  else if (bone.startsWith("upperarm")) c.y = box.max.y;
  return node.worldToLocal(c);
}

/** One action on the mixer: a loop, or one copy of a one-shot (`clip` is the rig's, the action may play a twin of it). */
interface Track { readonly clip: THREE.AnimationClip; readonly action: THREE.AnimationAction; weight: number; startedAt: number }

export class BramAnimator {
  private readonly mixer: THREE.AnimationMixer;
  private readonly brain: BramBrain;
  private readonly blinks: BlinkScheduler;
  private readonly wander: LookWander;
  private readonly tracks: Track[] = [];
  /** The copy playing the frame's one-shot, or null. */
  private shot: Track | null = null;
  private readonly driven = new Map<THREE.Object3D, Driven>();
  /** Each morphing mesh's influences as the mixer last left them (zeros until a clip keys them). */
  private readonly morphBase = new Map<THREE.Mesh, number[]>();
  private speech: Speech | null = null;
  private target: { dx: number; dy: number; mirrored: boolean } | null = null;
  private look: LookAngles = LOOK_AHEAD;
  private face: Partial<Record<BramMorph, number>> = {};
  private mouth = { open: 0, wide: 0 };
  private lastAction: BramFrame["action"] = null;
  private enterAt = -Infinity;
  private enterKind: "enter" | "hop" | null = null;
  private changedAt = 0;

  constructor(private readonly model: BramModel, rng: () => number, now: number) {
    this.mixer = new THREE.AnimationMixer(model.rig.scene);
    this.brain = new BramBrain(rng, clipSeconds(model.rig, CLIP_SECONDS));
    this.blinks = new BlinkScheduler(rng, now);
    this.wander = new LookWander(rng, now);
    const b = model.rig.bones;
    model.holder.updateMatrixWorld(true);
    for (const bone of ["head", "eye_L", "eye_R", "jaw", "upperarm_L", "upperarm_R"] as BramBone[]) {
      const o = b[bone];
      if (!o || this.driven.has(o)) continue;
      const pivot = pivotOf(bone, o);
      const side = Math.sign(o.localToWorld(pivot.clone()).x) || (bone.endsWith("_R") ? -1 : 1);
      this.driven.set(o, { node: o, pos: o.position.clone(), quat: o.quaternion.clone(), scale: o.scale.clone(), pivot, side });
    }
    for (const slots of Object.values(model.rig.morphs)) for (const s of slots ?? []) {
      if (!this.morphBase.has(s.mesh)) this.morphBase.set(s.mesh, s.mesh.morphTargetInfluences!.map(() => 0));
    }
  }

  /** A step came up. `entry` is how he appears: popping in, a small hop (a line straight after another), or neither. */
  start(mood: BramMood, kind: GuideKind, hasTarget: boolean, now: number, entry: "enter" | "hop" | null): void {
    this.brain.start(mood, kind, hasTarget, now);
    this.enterKind = entry;
    this.enterAt = now;
    this.changedAt = now;
    this.blinks.trigger(now + 250);
  }

  /** How he appears, timed from now (the first frame on screen). */
  enter(kind: "enter" | "hop", now: number): void { this.enterKind = kind; this.enterAt = now; this.changedAt = now; }

  say(speech: Speech | null): void { this.speech = speech; }

  /** The target relative to his eyes on screen (CSS px), or null to look at the player. */
  aim(t: { dx: number; dy: number; mirrored: boolean } | null, now: number): void {
    const had = this.target !== null;
    this.target = t;
    if (had !== (t !== null)) { this.brain.setTarget(t !== null); this.changedAt = now; }
  }

  nudge(now: number): void { this.brain.nudge(now); this.changedAt = now; }

  get mood(): BramMood { return this.brain.currentMood; }

  /** Advances one rendered frame. Returns true while something is moving enough to want the full frame rate. */
  update(now: number, dtSec: number): boolean {
    const talking = this.speech !== null && !this.speech.done(now);
    const frame = this.brain.update(now, talking);
    if (frame.action !== this.lastAction) { this.lastAction = frame.action; this.changedAt = now; }

    this.restoreMixerPose();
    const gestureOffsets = this.weigh(frame, now, dtSec);
    this.mixer.update(dtSec);
    this.keepMixerPose();
    this.faceAndMouth(frame, now, dtSec);
    this.pose(frame, gestureOffsets, now, dtSec);

    const acting = frame.action !== null && now - frame.action.at < frame.action.ms;
    const entering = this.enterKind !== null && now - this.enterAt < (this.enterKind === "enter" ? ENTER_MS : HOP_MS);
    return talking || acting || entering || now - this.changedAt < SETTLE_MS;
  }

  private turn(node: THREE.Object3D, q: THREE.Quaternion): void {
    turnAbout(node, q, this.driven.get(node)?.pivot ?? va.set(0, 0, 0));
  }

  dispose(): void { this.mixer.stopAllAction(); this.mixer.uncacheRoot(this.model.rig.scene); }

  private addTrack(clip: THREE.AnimationClip, plays: THREE.AnimationClip): Track {
    const action = this.mixer.clipAction(plays);
    action.setEffectiveWeight(0);
    const t: Track = { clip, action, weight: 0, startedAt: NaN };
    this.tracks.push(t);
    return t;
  }

  /** The loop's one action. */
  private loopTrack(clip: THREE.AnimationClip): Track {
    return this.tracks.find((t) => t.clip === clip) ?? this.addTrack(clip, clip);
  }

  /** A copy of a one-shot that has faded out, to start from its first frame without a snap (a twin when all still show). */
  private freeShot(clip: THREE.AnimationClip): Track {
    const copies = this.tracks.filter((t) => t.clip === clip);
    const free = copies.find((t) => t.weight < WEIGHT_EPS);
    if (free) return free;
    if (copies.length < MAX_TWINS) return this.addTrack(clip, clip.clone());
    return copies.reduce((a, b) => (b.weight < a.weight ? b : a));
  }

  /** Sets each action's weight toward the frame's wishes; returns the procedural offsets for a missing clip. */
  private weigh(frame: BramFrame, now: number, dt: number): Offsets {
    const clips = this.model.rig.clips;
    const a = frame.action;
    const oneShot = a ? clips[a.clip] ?? null : null;
    const loop = clips[frame.loop] ?? null;
    if (loop) {
      // the very first loop starts at full weight: no blend in from the bind pose
      const first = this.tracks.length === 0;
      const t = this.loopTrack(loop);
      if (first) t.weight = 1;
      if (!t.action.isRunning()) { t.action.setLoop(THREE.LoopRepeat, Infinity); t.action.play(); }
    }
    if (!oneShot || !a) this.shot = null;
    else if (this.shot?.clip !== oneShot || this.shot.startedAt !== a.at) {
      // a gesture (made again or new): a copy at rest weight starts from its first frame; the old one fades out
      const t = this.freeShot(oneShot);
      t.action.reset();
      t.action.setLoop(THREE.LoopOnce, 1);
      t.action.clampWhenFinished = true;
      t.action.play();
      t.startedAt = a.at;
      this.shot = t;
    }
    for (const t of this.tracks) {
      const want = t === this.shot ? 1 : t.clip === loop ? (this.shot ? 0 : 1) : 0;
      const rate = t === this.shot ? ACTION_IN_RATE : want === 0 && t.action.loop === THREE.LoopOnce ? ACTION_OUT_RATE : LOOP_RATE;
      t.weight = damp(t.weight, want, rate, dt);
      if (want === 0 && t.weight < WEIGHT_EPS) { t.weight = 0; if (t.action.loop === THREE.LoopOnce) t.action.stop(); }
      t.action.setEffectiveWeight(t.weight);
    }
    if (a && !oneShot) return gesture(a.clip, Math.min(1, (now - a.at) / a.ms));
    return REST;
  }

  /**
   * Nodes and shape keys the procedural layer moves go back to what the mixer last left on them before it writes the
   * frame: it skips every value that has not changed since its last write (and every node no clip animates).
   */
  private restoreMixerPose(): void {
    for (const d of this.driven.values()) { d.node.position.copy(d.pos); d.node.quaternion.copy(d.quat); d.node.scale.copy(d.scale); }
    for (const [mesh, base] of this.morphBase) { const inf = mesh.morphTargetInfluences!; for (let i = 0; i < base.length; i++) inf[i] = base[i]; }
  }

  /** What the mixer left, before the procedural layer adds to it. */
  private keepMixerPose(): void {
    for (const d of this.driven.values()) { d.pos.copy(d.node.position); d.quat.copy(d.node.quaternion); d.scale.copy(d.node.scale); }
    for (const [mesh, base] of this.morphBase) { const inf = mesh.morphTargetInfluences!; for (let i = 0; i < base.length; i++) base[i] = inf[i]; }
  }

  private faceAndMouth(frame: BramFrame, now: number, dt: number): void {
    const morphs = this.model.rig.morphs;
    for (const m of ["smile", "frown", "brows_up", "brows_angry"] as const) this.face[m] = damp(this.face[m] ?? 0, frame.face[m] ?? 0, FACE_RATE, dt);
    const want = this.speech ? this.speech.mouth(now) : { open: 0, wide: 0 };
    this.mouth.open = damp(this.mouth.open, want.open, MOUTH_RATE, dt);
    this.mouth.wide = damp(this.mouth.wide, want.wide, MOUTH_RATE, dt);
    const blink = this.blinks.weight(now);
    const set = (m: BramMorph, v: number) => { for (const s of morphs[m] ?? []) { const inf = s.mesh.morphTargetInfluences!; inf[s.index] = Math.max(inf[s.index], v); } };
    set("blink", blink);
    set("mouth_open", this.mouth.open);
    set("mouth_wide", this.mouth.wide);
    for (const m of ["smile", "frown", "brows_up", "brows_angry"] as const) set(m, this.face[m] ?? 0);
    const b = this.model.rig.bones;
    if (!morphs.blink) for (const e of [b.eye_L, b.eye_R]) if (e) e.scale.y *= 1 - (1 - EYE_BLINK_SCALE) * blink;
  }

  /** Holder hops and squashes, then the look-at and gesture turns on the head, eyes, jaw and arms. */
  private pose(frame: BramFrame, g: Offsets, now: number, dt: number): void {
    const { holder, rig } = this.model;
    const e = this.enterKind === "enter" ? entrance((now - this.enterAt) / ENTER_MS) : this.enterKind === "hop" ? hop((now - this.enterAt) / HOP_MS) : { rise: 0, squash: 1 };
    const sq = e.squash * g.squash;
    holder.position.y = e.rise + g.hop;
    holder.scale.set(1 / Math.sqrt(sq), sq, 1 / Math.sqrt(sq));
    holder.rotation.x = g.lean;

    let want = LOOK_AHEAD;
    if (frame.look === "target" && this.target) want = lookAngles(this.target.dx, this.target.dy, this.target.mirrored);
    const w = this.wander.at(now);
    if (w.moved) this.blinks.trigger(now);
    const eyeYaw = want.eyeYaw + (frame.look === "player" ? w.yaw : 0), eyePitch = want.eyePitch + (frame.look === "player" ? w.pitch : 0);
    this.look = {
      headYaw: damp(this.look.headYaw, want.headYaw, HEAD_RATE, dt), headPitch: damp(this.look.headPitch, want.headPitch, HEAD_RATE, dt),
      eyeYaw: damp(this.look.eyeYaw, eyeYaw, EYE_RATE, dt), eyePitch: damp(this.look.eyePitch, eyePitch, EYE_RATE, dt),
    };
    holder.updateMatrixWorld(true);
    const b = rig.bones;
    const noMouth = !rig.morphs.mouth_open && !b.jaw;
    if (b.head) {
      const pitch = this.look.headPitch + g.headPitch + (noMouth ? talkBob(this.mouth.open) : 0);
      this.turn(b.head, qa.setFromAxisAngle(Y, this.look.headYaw + g.headYaw).multiply(qb.setFromAxisAngle(X, -pitch)).multiply(qc.setFromAxisAngle(Z, g.headRoll)));
    }
    for (const eye of [b.eye_L, b.eye_R]) if (eye) this.turn(eye, qa.setFromAxisAngle(Y, this.look.eyeYaw).multiply(qb.setFromAxisAngle(X, -this.look.eyePitch)));
    if (b.jaw && !rig.morphs.mouth_open) this.turn(b.jaw, qa.setFromAxisAngle(X, JAW_MAX * this.mouth.open));
    // an arm raises out and up: a turn about Z toward its own side
    for (const [arm, u] of [[b.upperarm_R, g.armR], [b.upperarm_L, g.armL]] as const) {
      if (arm && u > 0) this.turn(arm, qa.setFromAxisAngle(Z, (this.driven.get(arm)?.side ?? 1) * ARM_MAX * u));
    }
  }
}

/**
 * Turns `node` by `q` (about world axes) around `pivot` (a point in its own space), on top of its current pose:
 * world' = T(p) · q · T(−p) · world, then back into its parent's space.
 */
function turnAbout(node: THREE.Object3D, q: THREE.Quaternion, pivot: THREE.Vector3): void {
  va.copy(pivot).applyMatrix4(node.matrixWorld);
  ma.makeTranslation(-va.x, -va.y, -va.z).premultiply(mb.makeRotationFromQuaternion(q)).premultiply(mb.makeTranslation(va.x, va.y, va.z));
  ma.multiply(node.matrixWorld);
  if (node.parent) ma.premultiply(mb.copy(node.parent.matrixWorld).invert());
  ma.decompose(node.position, node.quaternion, node.scale);
  node.updateMatrixWorld(true);
}
